import { toHttpsUrl } from './leapbot-chat.util';

/** A piece of inline content of an answer; never carries HTML. */
export type LeapBotInline =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; url: string };

/** A block of an answer, ready to be rendered with plain Angular bindings (no `innerHTML`). */
export type LeapBotBlock =
  | { kind: 'paragraph'; inlines: LeapBotInline[] }
  | { kind: 'heading'; level: number; inlines: LeapBotInline[] }
  | { kind: 'code'; language: string; code: string }
  | { kind: 'list'; ordered: boolean; items: LeapBotInline[][] };

const INLINE_PATTERN =
  /!\[([^\]]*)\]\([^)]*\)|\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)|\*\*([^*]+)\*\*|`([^`]+)`|(?<![\w*])\*([^*\s][^*]*)\*(?![\w*])|(?<![\w_])_([^_\s][^_]*)_(?![\w_])/g;

/**
 * Parses one line of Markdown into inline pieces. Links are kept only for `https://` URLs (any
 * other scheme renders as its plain text) and images are never loaded: they become their alt text.
 *
 * @param source - Untrusted Markdown text coming from LeapBot.
 * @example
 * parseInline('Mira [esto](https://x.io) y [eso](javascript:alert(1))');
 * // text, link(https://x.io), text, text('eso')
 */
export function parseInline(source: string): LeapBotInline[] {
  const result: LeapBotInline[] = [];
  let cursor = 0;
  const push = (inline: LeapBotInline): void => {
    const last = result[result.length - 1];
    if (inline.kind === 'text' && last?.kind === 'text') last.text += inline.text;
    else result.push(inline);
  };
  for (const match of source.matchAll(INLINE_PATTERN)) {
    if (match.index > cursor) push({ kind: 'text', text: source.slice(cursor, match.index) });
    cursor = match.index + match[0].length;
    const [, imageAlt, linkText, linkUrl, bold, code, starItalic, underscoreItalic] = match;
    if (match[0].startsWith('![')) push({ kind: 'text', text: imageAlt ?? '' });
    else if (linkText !== undefined) {
      const url = toHttpsUrl(linkUrl);
      push(url ? { kind: 'link', text: linkText, url } : { kind: 'text', text: linkText });
    } else if (bold !== undefined) push({ kind: 'bold', text: bold });
    else if (code !== undefined) push({ kind: 'code', text: code });
    else push({ kind: 'italic', text: starItalic ?? underscoreItalic ?? '' });
  }
  if (cursor < source.length) push({ kind: 'text', text: source.slice(cursor) });
  return result;
}

/**
 * Parses an answer into blocks: paragraphs, headings, fenced code, and bullet or numbered lists.
 * The result contains only data, so templates render it without executing anything.
 *
 * @param markdown - Untrusted Markdown text coming from LeapBot.
 */
export function parseMarkdown(markdown: string): LeapBotBlock[] {
  const blocks: LeapBotBlock[] = [];
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: LeapBotInline[][] } | null = null;

  const flushParagraph = (): void => {
    if (paragraph.length > 0) blocks.push({ kind: 'paragraph', inlines: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };
  const flushList = (): void => {
    if (list) blocks.push({ kind: 'list', ...list });
    list = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      flushParagraph();
      flushList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) code.push(lines[i++]);
      blocks.push({ kind: 'code', language: fence[1], code: code.join('\n') });
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'heading', level: heading[1].length, inlines: parseInline(heading[2]) });
    } else if (bullet || numbered) {
      flushParagraph();
      const ordered = numbered !== null;
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push(parseInline((bullet ?? numbered)?.[1] ?? ''));
    } else if (line.trim() === '') {
      flushParagraph();
      flushList();
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}
